import { SECTION_TYPES } from '@/content/values/sections';
import { WidgetTeacherHomework } from '@/homework/models/widget-teacher-homework.model';
import { HOMEWORK_KEYS, HOMEWORK_STATUS, _HOMEWORKS_STORE, HOMEWORKS_EVENTS } from '@/homework/values/homeworks.values';
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { Section } from '../models/section.model';
import { COURSES_EVENTS } from '../values/courses-events.values';
import { ANNOTATION_TYPES } from '../values/courses.values';
import KEYS from '@/values/keys';
import { HomeworkMixin } from '@/homework/mixins/homework.mixin';
import ToolService from '@/providers/tools';
import { HomeworksService } from '@/homework/services/homeworks.service';
import { IWhiteboardUser } from '@/homework/interfaces/whiteboard-user.interface';
import { CHANGE_PRESENTATION_TASK_USER_EVENT, SEND_CURRENT_SHARED_TASK_EVENT } from '@/content/values/ed-presentation-mode.values';
import { ScreenMirroringMixin } from '@/screen-mirroring/mixins/screen-mirroring.mixin';
import { ErrorProvider } from '@/shared/providers/errors.provider';
import { Errors } from '@/shared/values/errors.values';
import { Error } from '@/shared/interfaces/error.interface';

@Component({})
export class HomeworkPdfDraw extends Mixins(ScreenMirroringMixin, HomeworkMixin){

  @Prop() readonly section: Section;
  @Prop() readonly responseDraft: WidgetTeacherHomework;
  @Prop() readonly courseId: number;
  @Prop() readonly response: any;
  @Prop() readonly canResponse: boolean;
  @Prop() readonly isFeedback: boolean;
  @Prop() readonly feedback: Section;

  @Prop() readonly history: any;

  public isSaving: boolean = false;
  public isSavedDraft = {
    pagesDrawn: [],
    status: false
  };
  public pdfLoaded: boolean = false;
  public pdfChanged: boolean = false;
  public timeIntervalDraft: any = null;
  public pagesDrawn: number[] = [];
  private board: string = '';
  private boardGroupHash: string = '';
  private homeworkId: number = 0;
  private isLoading: boolean = true;
  private isValidateWhiteboard: boolean = false;

  get canSave() {
    if(this.pdfLoaded && !this.isSaving) {
      if(this.responseDraft) {
        if(this.isSavedDraft.status && this.pdfChanged) {
          return true;
        } else if (this.hasPagesDrawn) {
          return true;
        }
      } else if(this.hasPagesDrawn) {
        return true;
      } else if(this.isSavedDraft.status && this.pdfChanged) {
        return true;
      }
    }
    return false;
  }

  get canSubmit() {
    if(this.pdfLoaded && !this.isSaving) {
      if(this.responseDraft) {
        return true;
      } else if (this.hasPagesDrawn) {
        return true;
      }
    }
    return false;
  }

  get canSendResponse(): boolean {

    if( this.isScreenMirroringBookModeActive ) {
      return false;
    }

    if(typeof this.canResponse !== 'undefined') {
      return this.canResponse;
    } else {
      return this.section.status != HOMEWORK_STATUS.GRADED && this.section.status != HOMEWORK_STATUS.FINISHED;
    }
  }

  get responseHomework(): Section {
    if(this.isFeedback) {
      return this.feedback;
    }
    if(this.response) {
      return { ...this.response, assetsInfo: [this.response.homeworkAssetInfo] };
    }
    return null;
  }

  get isShowTeacher(): boolean{
    return (this.section.status === HOMEWORK_STATUS.DRAFT || !this.section.status) && this.$store.state.AuthStore.role === KEYS.ROLES.PUBLISHER;
  }

  get hasPagesDrawn(): boolean {
    return this.pagesDrawn.length > 0;
  }

  get numberPages() : number {
    return this.$store.state.HomeworksStore.numberPages;
  }

  get isPublisher() : boolean {
    return this.$store.state.AuthStore.role === KEYS.ROLES.PUBLISHER;
  }

  get selectedBoardInfo() : {[key: string]: any} {
    return this.$store.state.HomeworksStore.selectedBoardInfo;
  }

  get isPresentationStudentMode() : boolean {
    return this.$store.state.CourseStore.isPresentationStudentMode;
  }

  get isPresentationTeacherMode() : boolean {
    return this.$store.state.CourseStore.isPresentationTeacherMode;
  }

  get currentBoardUserId() : number {
    return this.$store.state.MeetingsStore.currentBoardUser.id;
  }

  get userLoginId() : number {
    return this.$store.state.AuthStore.user.id;
  }

  onChangePdf({drawn, pagesDrawn}) {
    this.pdfChanged = true;
    this.pagesDrawn = pagesDrawn;
    this.setIntervalDraft();
    this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.CHANGE_CONFIRM_CLOSE_HOMEWORK_VIEWER, true);
  }

  onLoadPdf() {
    this.pdfLoaded = true;
  }

  saveInDraft(): Promise<void> {
    if(this.canSave) {
      this.isSaving = true;
      this.unSetIntervalDraft();
      this.$EdToast.create({ msg: `${this.$i18n.t('common.saving')}...` });
      return new Promise ((resolve, reject) => {
        setTimeout(async () => {
          try {
            let req = this.getRequestDraftData();
            await this.createStudentDraftHomework(
              this.courseId,
              req.homework,
              req.file
            );
            this.pdfChanged = false;
            if(!this.isSavedDraft.status) {
              this.isSavedDraft.status = true;
              this.isSavedDraft.pagesDrawn = this.pagesDrawn;
            }
            setTimeout(() => {
              this.isSaving = false;
              resolve();
            }, 1300);
          } catch(err) {
            this.isSaving = false;
            this.$EdToast.hide();
            this.$EdModal.createMessageModal({
              title: this.$i18n.t('courses.errors.errorTitle'),
              body: this.$i18n.t('courses.errors.errorResponseWriteObject'),
              type: KEYS.EVENT_TYPES.DANGER
            });
            reject();
          }
        }, 400);
      });
    }
  }

  getRequestDraftData() {
    let pdfDraw: any = this.$refs.EdPdfDraw;
    let original = this.responseDraft ? this.responseDraft.homeworkDraftAssetInfo.hash : null;
    let currentPagesDrawn = pdfDraw.getDrawnPages();
    let pages = [];

    /* We check if the previous draft was changed */
    if(this.isSavedDraft.status) {
      currentPagesDrawn.forEach(p => {
        let found = this.isSavedDraft.pagesDrawn.findIndex(pd => pd == p) !== -1;
        if(!found) this.isSavedDraft.pagesDrawn.push(p);
      });
      pages = ToolService.orderByNumber(this.isSavedDraft.pagesDrawn);
    } else {
      pages = currentPagesDrawn;
    }

    let request: any = {
      homework: {
        name: this.section.name,
        classId: this.section.classId,
        sectionId: this.section.sectionId,
        type: SECTION_TYPES.LESSON_PDF.code,
        metadata: {
          type: ANNOTATION_TYPES.PDF_ANNOTATION,
          pages,
        }
      },
      file: pdfDraw.getPDFPages(pages)
    }
    if(original) request.homework.metadata.original = original;
    return request;
  }

  get studentId(){
    if(this.$store.state.AuthStore.role === KEYS.ROLES.BUYER){
      return this.$store.state.AuthStore.user.id;
    }else {
      return this.section.studentId;
    }
  }

  async saveHomework() {
    if(this.canSubmit) {
      this.$EdModal.createConfirmationModal(
        {
          body: this.$i18n.t('common.confirmSavePdf'),
          action: async () => {
            this.$EdToast.create({ msg: `${this.$i18n.t('common.sendingResponse')}...` });
            setTimeout(async () => {
              try {
                this.isSaving = true;
                this.unSetIntervalDraft();
                let pdfDraw: any = this.$refs.EdPdfDraw;
                let file = await pdfDraw.getCurrentPdf(HOMEWORK_KEYS.RESPONSE_STUDENT);
                await this.createStudentHomework(this.courseId, {
                  id: this.section.sectionId,
                  sectionId: this.section.sectionId,
                  classId: this.section.classId,
                  name: this.section.name,
                  description: this.section.description,
                  type: SECTION_TYPES.LESSON_PDF.code
                }, file)
                this.$EdModal.closeModal();
              } catch(err) {
                this.isSaving = false;
                this.$EdToast.hide();
                this.$EdModal.createMessageModal({
                  title: this.$i18n.t('courses.errors.errorTitle'),
                  body: this.$i18n.t('courses.errors.errorResponseWriteObject'),
                  type: KEYS.EVENT_TYPES.DANGER
                });
                throw err;
              }
            }, 400);
          }
        }
      );
    }
  }

  async saveAndExit() {
    await this.saveInDraft();
    this.$EdModal.closeModal();
  }

  public async saveWhiteboardHomework(){
    if(!this.isSaving && this.boardGroupHash) {
      let usersMadeAnnotations = await this.obtainUsersMadeAnnotations(this.boardGroupHash);

      if(usersMadeAnnotations && usersMadeAnnotations.length > 0){
        this.saveTaskWithAnnotationOptions(this.obtainSaveOptions(usersMadeAnnotations));
      } else {
        this.openModalWithoutAnnotations();
      }
    }
  }

  setIntervalDraft() {
    if(!this.timeIntervalDraft) {
      this.timeIntervalDraft = setInterval(() => {
        this.saveInDraft();
      }, 300000);
    }
  }

  unSetIntervalDraft() {
    if(this.timeIntervalDraft) {
      clearInterval(this.timeIntervalDraft);
      this.timeIntervalDraft = null;
    }
  }

  created() {
    this.$edEventBus.$on(COURSES_EVENTS.SAVE_AND_CLOSE_HOMEWORK, this.saveAndExit);
  }

  beforeDestroy() {
    this.unSetIntervalDraft();
    this.$edEventBus.$off(COURSES_EVENTS.SAVE_AND_CLOSE_HOMEWORK, this.saveAndExit);
  }

  public async callPdf() {
    this.isLoading = true;

    if(this.selectedBoardInfo && this.selectedBoardInfo.boardGroupHash && this.isPresentationStudentMode && this.currentBoardUserId !== this.userLoginId){
      await this.fillBoardInfo(this.selectedBoardInfo);
      this.isLoading = false
      return;
    }

    this.callBoardService()
      .then(async (response) => {

        await this.fillBoardInfo(response);
        this.$edEventBus.$emit(HOMEWORKS_EVENTS.REALOAD_TEACHER_HOMEWORKS, this.studentId);
        if(this.isPublisher && this.isPresentationTeacherMode){
          this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_SELETED_BOARD_INFO, response);
          this.$edEventBus.$emit(SEND_CURRENT_SHARED_TASK_EVENT);
        }
      })
      .catch(error => {
        let errorDefaultDetail = {
          title: this.$i18n.t('courses.errors.errorTitle'),
          body: this.$i18n.t('courses.errors.errorOpenWhiteboardObject'),
          type: KEYS.EVENT_TYPES.DANGER,
          action: () => {
            this.$EdModal.closeModal();
          }
        };

        if(error && error.status === 400){
          errorDefaultDetail.body = this.$i18n.t('courses.errors.errorNotFoundOpenWhiteboardObject');
          errorDefaultDetail.action = () => {
            this.$EdModal.closeModal();
            this.$edEventBus.$emit(HOMEWORKS_EVENTS.RELOAD_STUDENT_HOMEWORKS_TEACHER_WIDGET);
            this.$edEventBus.$emit(HOMEWORKS_EVENTS.RELOAD_SECTION_TEACHER_WIDGET);
          };
        }

        this.$EdModal.createMessageModal(errorDefaultDetail);
      })
      .finally(() => this.isLoading = false);
  }

  private async fillBoardInfo(boardInfo : any) {
    this.boardGroupHash = boardInfo.boardGroupHash;
    this.homeworkId = boardInfo.homeworkId;
    this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_NUMBER_PAGES, boardInfo.numberPages);
    this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.ADD_BOARD_HOMEWORK_HASH_USERS, { boardGroupHash: this.boardGroupHash, homeworkId: this.homeworkId, users: [] });
  }

  public cancelLoading():void{
    this.isLoading = false;
  }

  private obtainOtherUsersMadeAnnotations(users: IWhiteboardUser[]):number[]{
    if(!users){
      return [];
    }

    return users
      .filter(user => user.id != this.studentId)
      .map(user => user.id);
  }

  private obtainAnnotationStudentUser(users: IWhiteboardUser[]):number[]{
    if(!users){
      return [];
    }

    return users
      .filter(user => user.id === this.studentId)
      .map(user => user.id);
  }

  public obtainSaveOptions(users: IWhiteboardUser[]) : any {

    let otherUsersMadeAnnotations = this.obtainOtherUsersMadeAnnotations(users);
    let studentUserMadeAnnotations = this.obtainAnnotationStudentUser(users);

    let options = {
      isOnlyTeacher: false,
      data: {
        body: this.$i18n.t('common.confirmSavePdf'),
        hasOptionRadio: false,
        firstOptionRadioText: null,
        secondOptionRadioText: null
      }
    };

    if(studentUserMadeAnnotations.length === 0 && otherUsersMadeAnnotations.length > 0){
      options.data.body = this.$i18n.t('homeworks.homeworkOnlyTeacherAnnotations');
      options.isOnlyTeacher = true;
    }else if(studentUserMadeAnnotations.length === 1 && otherUsersMadeAnnotations.length > 0){
      options.data.body = this.$i18n.t('homeworks.homeworkManyAnnotations');
      options.data.hasOptionRadio = true;
      options.data.firstOptionRadioText = this.$i18n.t('homeworks.sendOnlyMyAnnotations');
      options.data.secondOptionRadioText = this.$i18n.t('homeworks.sendAllAnnotations');
    }

    return options;
  }

  private saveTaskWithAnnotationOptions(options : any) : void {
    this.$EdModal.createConfirmationModal(
      {
        ...options.data,
        action: (answer) => {
          let usersAnnotations = answer && answer.optionRadioValue === 'first' && !answer.optionSelected && !options.isOnlyTeacher ? [this.studentId] : null;
          this.isSaving = true;
          this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_SAVING_HOMEWORK, this.isSaving);
          this.$EdToast.create({ msg: `${this.$i18n.t('common.sendingResponse')}...` });

          this.createStudentHomework(this.courseId, {
            id: this.homeworkId,
            sectionId: this.section.sectionId,
            classId: this.section.classId,
            name: this.section.name,
            description: this.section.description,
            type: SECTION_TYPES.LESSON_BOARD.code,
            usersAnnotations
          }).then(response => {
            if (this.isPresentationStudentMode){
              this.$edEventBus.$emit(CHANGE_PRESENTATION_TASK_USER_EVENT);
              return;
            }
            this.$edEventBus.$emit(COURSES_EVENTS.CLOSE_HOMEWORK_VIEWER);
            this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.REMOVE_BOARD_HOMEWORK_HASH_USERS, this.boardGroupHash);
          }).catch(error => {
            let message = this.$i18n.t('courses.errors.errorResponseWriteObject');
            let action = undefined;
            if(error.data.errors){
              let errDetail = ErrorProvider.getErrorsOfTheRequest(error.data.errors)[0]
              if( Errors.HW001.code === errDetail.code){
                //Refresh the tasks while showing the error message
                ({ message, action } = this.refreshFromError(message, errDetail.message, action));
              }
            }
            this.showErrorModal(action, message);
          }).finally(() => {
            this.isSaving = false;
            this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_SAVING_HOMEWORK, this.isSaving);
          });
        }
      }
    )
  }

  private showErrorModal(action: any, message) {
    this.$EdToast.hide();
    this.$EdModal.createMessageModal({
      action: action,
      title: this.$i18n.t('courses.errors.errorTitle'),
      body: message,
      type: KEYS.EVENT_TYPES.DANGER
    });
  }

  private refreshFromError(message, messageError, action: any) {
    message = messageError;
    action = async () => {
      this.$edEventBus.$emit(COURSES_EVENTS.CLOSE_HOMEWORK_VIEWER); //Close the task viewer
      this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.REMOVE_BOARD_HOMEWORK_HASH_USERS, this.boardGroupHash); //Remove archived hash
      this.$edEventBus.$emit(COURSES_EVENTS.REFRESH_ON_SECTION_UPDATED, { courseId: this.courseId, classId: this.section.classId, sectionId: this.section.sectionId } );
    };
    return { message, action };
  }

  private async callBoardService() : Promise<any> {
    if (this.$store.state.AuthStore.role === KEYS.ROLES.PUBLISHER) {
      return HomeworksService.obtainTeacherWhiteboardHomeworkHash({
        classId: this.section.classId,
        sectionId: this.section.sectionId,
        studentId: this.studentId
      });
    }
    let response = await HomeworksService.obtainStudentWhiteboardHomeworkHash({classId: this.section.classId, sectionId: this.section.sectionId});
    const sectionVersion = this.section.version ?? this.section.section?.version;
    if(response.version !== sectionVersion) {
      let action = undefined;
      let message = undefined;
      const errorMessage = this.$i18n.t('errors.codes.HW001');
      ({ message, action } = this.refreshFromError(message, errorMessage, action));
      this.showErrorModal(action, message);
    }
    this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_SAVING_DRAFT,   {
                      classId : this.section.classId,
                      courseId : this.courseId,
                      sectionId: this.section.sectionId,
                      responseDraft: response.responseDraft,
                      student: {id : this.studentId}
                    });
    return response;
  }
}
